/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";
import { readCookiePrefs } from "@/utils/cookiePrefs";

/**
 * Sentry's page-speed measurements and session replays follow the visitor's
 * analytics choice (Session 515 review, SU-2).
 *
 * The Deskshare Privacy Policy, Section 5: "Error monitoring runs regardless
 * of your cookie choices ... If you turn on analytics cookies, the provider
 * also takes page-speed measurements for a sample of visits and, when an
 * error occurs, receives a session replay". Until this release tracing (10%)
 * and error replays (25%) ran for every visitor.
 *
 * So:
 *   - error reports: always (Sentry.init, unchanged);
 *   - tracing: the sampler answers 0 unless analytics is granted at the
 *     moment a page load or navigation starts;
 *   - replay: the integration is added only once analytics is granted, and
 *     stopped as soon as it is withdrawn ("ds:cookie-consent-saved").
 *
 * What must still NOT happen: a trace or a replay buffer for a visitor with no
 * stored choice, with analytics turned off, or with a stale or expired record
 * (readCookiePrefs() returns null for those); a replay that keeps recording
 * after analytics is withdrawn.
 */

/** The replay options; the masking rules are unchanged from before. */
export const REPLAY_OPTIONS = {
  maskAllText: true, // mask all visible text before sending to Sentry
  blockAllMedia: true, // block all media elements in session replays
} as const;

export function analyticsConsented(): boolean {
  return readCookiePrefs()?.analytics === true;
}

/** A tracesSampler that samples at `rate` only while analytics is granted. */
export function consentTracesSampler(rate: number): () => number {
  return () => (analyticsConsented() ? rate : 0);
}

let replayRunning = false;

/** Start or stop the replay to match the stored analytics choice. */
export function applyReplayConsent(): void {
  const replay = Sentry.getReplay();
  if (analyticsConsented()) {
    if (replayRunning) return;
    if (replay) {
      // Added earlier, then stopped by a withdrawal: buffer for errors again.
      replay.startBuffering();
    } else {
      Sentry.addIntegration(Sentry.replayIntegration(REPLAY_OPTIONS));
    }
    replayRunning = true;
  } else if (replay && replayRunning) {
    void replay.stop();
    replayRunning = false;
  }
}

/** Called once, after Sentry.init. */
export function installSentryConsent(): void {
  applyReplayConsent();
  window.addEventListener("ds:cookie-consent-saved", applyReplayConsent);
}

/** Test seam: forget the running state between tests. */
export function resetSentryConsentForTests(): void {
  replayRunning = false;
  window.removeEventListener("ds:cookie-consent-saved", applyReplayConsent);
}
